import React, { useCallback, useEffect, useState, ReactNode, useContext } from "react";
import "./Sidebar.scss";
import { Icon, ThemedIcon } from "@me8eon/icons";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { EnvironmentConfig } from "@me8eon/config";
import { AiAssistContext } from "me-at-eon/src/components/ai-assist-refactored/AiAssist.context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@me8eon/translation";
import { FocusTrap } from "@me8eon/focus_trap";
import { useUserData } from "@me8eon/react_authentication";
import { useWindowProperties } from "me-at-eon/src/context/WindowContext";
import { useAsyncSessionAndMessages, useSessionAndMessageOps } from "@me8eon/sessions_messages";
import { isDataAs, useQuestionSuggestions } from "@me8eon/async";
import { useFeatureFlag } from "@me8eon/featureflags";
import { useAuthenticatedFetch } from "@me8eon/new_ai_utils/src/hooks/useAuthenticatedFetch";
import { deleteAllConversations, listConversations } from "@me8eon/new_ai_utils/src/api/conversations";
import { CONVERSATION_UPDATED_EVENT, defaultChatConfig, requestActivitiesPanelOpen } from "@me8eon/new_ai_utils/src/config/chat.config";
import { ROLE_PRODUCT_TEAM_DASHBOARD } from "me-at-eon/src/pages/product-owner-dashboard/ProductOwnerDashboard.constants";
import { ROLE_AIDWOPS_DASHBOARD } from "me-at-eon/src/pages/aidwops/expert-dashboard/ExpertDashboard.constants";
// import { useChat } from "@me8eon/new_ai_utils/src/hooks/useChat";

interface SidebarProps {
    sidebar: ReactNode;
    children: ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

interface SidebarBtnProps {
    isExpanded: boolean;
    label: string;
    onClick?: () => void;
    noCursor?: boolean;
    icon: string;
    tooltip: string;
    deleteIcon?: boolean;
    tourId?: string;
}

const SidebarBtn = ({ isExpanded, label, onClick, icon, tooltip, noCursor, deleteIcon, tourId }: SidebarBtnProps) => {
    const translate = useTranslation();
    const { loggedIn } = useUserData();

    const [state] = useAsyncSessionAndMessages();
    const { deleteChild } = useSessionAndMessageOps();
    const authenticatedFetch = useAuthenticatedFetch();
    const [hasNewAiSessions, setHasNewAiSessions] = useState(false);

    const refreshHasNewAiSessions = useCallback(async () => {
        if (!deleteIcon || !loggedIn) {
            setHasNewAiSessions(false);
            return;
        }
        try {
            const payload = await listConversations(authenticatedFetch, defaultChatConfig.conversationsEndpoint, { limit: 1 });
            setHasNewAiSessions(payload.conversations.length > 0);
        } catch (error) {
            console.warn("Could not check for new-AI conversations; clear-all icon may be hidden.", error);
            setHasNewAiSessions(false);
        }
    }, [authenticatedFetch, deleteIcon, loggedIn]);

    useEffect(() => {
        void refreshHasNewAiSessions();
    }, [refreshHasNewAiSessions]);

    useEffect(() => {
        window.addEventListener(CONVERSATION_UPDATED_EVENT, refreshHasNewAiSessions);
        return () => window.removeEventListener(CONVERSATION_UPDATED_EVENT, refreshHasNewAiSessions);
    }, [refreshHasNewAiSessions]);

    // Legacy-only threshold (>2) is preserved as-is; new-AI conversations use their
    // own presence check since they aren't part of `parent.sessions` at all.
    const showDeleteIcon = (isDataAs(state) && state.data.parent.sessions.length > 2) || hasNewAiSessions;
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    async function deleteAllSessions() {
        if (isDeletingAll) return;
        setIsDeletingAll(true);
        try {
            if (isDataAs(state)) {
                const ids = state.data.parent.sessions.map(session => session.id);
                if (ids.length > 0) await deleteChild(...ids);
            }
            await deleteAllConversations(authenticatedFetch, defaultChatConfig.conversationsEndpoint);
            window.dispatchEvent(new CustomEvent(CONVERSATION_UPDATED_EVENT));
        } catch (error) {
            console.error("Failed to delete new-AI conversations", error);
        } finally {
            setIsDeletingAll(false);
        }
    }

    return (
        <button className={`sidebar-btn ${noCursor ? "sidebar-btn--no-cursor" : ""} ${isExpanded && icon === "arrow_left" ? "sidebar-btn--align-right" : ""}`} onClick={onClick} title={tooltip} id={tourId}>
            {/* {icon === "home_add_solution_alt" || icon === "support" || icon === "all_in_one" ? <ThemedIcon name={icon} /> : <Icon name={icon} />} */}
            <EonUiIcon name={icon} scheme={icon === "home_add_solution_alt" || icon === "call-center" || icon === "all_in_one" ? "red500" : "neutral800"} />
            {isExpanded && (
                <div className="label-with-icon">
                    <span className={`sidebar-btn-label ${noCursor ? "sidebar-label--no-cursor" : ""}`}>{label}</span>
                    {showDeleteIcon && deleteIcon && (
                        <span
                            className="sidebar-btn-delete-icon"
                            title={translate("chatBot.sessions.clearAll")}
                            onClick={(event) => { event.stopPropagation(); void deleteAllSessions(); }}
                            aria-disabled={isDeletingAll}
                        >
                            {isDeletingAll
                                ? <span className="sidebar-btn-delete-icon-spinner" data-testid="sidebar.delete.all.spinner" aria-hidden="true" />
                                : <Icon name="trash_bin_small" style={{ width: "16px", height: "16px" }} />}
                        </span>
                    )}
                </div>
            )}
        </button>
    )
}

export const Sidebar = ({ sidebar, children, open: controlledOpen, onOpenChange }: SidebarProps) => {
    const { screenWidth } = useWindowProperties();
    const isMobile = screenWidth < 1024;

    const [internalOpen, setInternalOpen] = useState(!isMobile);
    const open = controlledOpen ?? internalOpen;
    const setOpen = (nextOpen: boolean | ((prev: boolean) => boolean)) => {
        const resolved = typeof nextOpen === 'function' ? nextOpen(open) : nextOpen;
        setInternalOpen(resolved);
        onOpenChange?.(resolved);
    };
    const t = useTranslation();
    const navigate = useNavigate();
    const { handleResetChat, setCurrentAgent } = useContext(AiAssistContext);
    const { featureFlagEnabled, loggedIn, actualRoles = [] } = useUserData();
    const isAdmin = localStorage.getItem("admin") === "true";
    const [, setQs] = useQuestionSuggestions();

    if (!loggedIn) {
        return children;
    }

    const handleClick = (isSupport?: boolean) => {
        if (isSupport) {
            navigationClicked("/new-ai", { autoSubmitQuestion: t("itSupportPrompt"), timestamp: Date.now() });
            return;
        }
        window.location.href = `mailto:${EnvironmentConfig.meAtEonContactEmail}`;
    };

    const handleActivitiesClick = () => {
        requestActivitiesPanelOpen();
        setOpen(false);
    };

    const closeForMobile = () => {
        isMobile && setOpen(false);
    }

    const navigationClicked = (link: string, state?: any) => {
        handleResetChat();
        closeForMobile();
        setCurrentAgent("");
        setQs([]);
        navigate(link, { state });
    }

    return (
        <div className="sidebar-layout-new">
            <FocusTrap>
                <aside
                    id="beta-left-sidebar"
                    className={`sidebar ${open ? "open" : ""}`}
                >
                    <div className="sidebar-header">
                        <SidebarBtn
                            isExpanded={open}
                            label=""
                            onClick={() => setOpen(!open)}
                            icon={`${open ? "arrow_left" : "arrow_right"}`}
                            tooltip={`${open ? t("chatBot.close") : t("chatBot.open")}`}
                        />
                    </div>
                    {/* 1. New Chat (Fixed at top, does not hide on scroll) */}
                    <div className="sidebar-fixed-top" id="tileTour10">
                        <SidebarBtn
                            isExpanded={open}
                            label={t("chatBot.newChat")}
                            icon="home_add_solution_alt"
                            tooltip={t("chatBot.newChat")}
                            onClick={() => navigationClicked(featureFlagEnabled ? "/new-ai" : "/ai", { startNewChat: true })}
                        />
                        {open && <hr className="sidebar-divider" />}
                    </div>
                    <div className="sidebar-body">
                        {/* 2. Recent Chat */}
                        <div>
                            <SidebarBtn
                                isExpanded={open}
                                label={t(featureFlagEnabled ? "chatBot.sessions.recentChatTitle" : "chatBot.recentChats")}
                                onClick={() => setOpen(true)}
                                icon={`chat`}
                                tooltip={t(featureFlagEnabled ? "chatBot.sessions.recentChatTitle" : "chatBot.recentChats")}
                                noCursor={open}
                                deleteIcon={!featureFlagEnabled}
                            />
                            <div>
                                {open ? sidebar : null}
                            </div>
                        </div>
                    </div>
                    <div className="sidebar-footer">
                        {open && <hr className="sidebar-divider sidebar-divider--desktop-hidden" />}
                        <div className="btn-desktop-hidden">
                            <SidebarBtn
                                isExpanded={open}
                                label={t("chatBot.rightSidebar.panelLabel")}
                                icon="all_in_one"
                                tooltip={t("chatBot.rightSidebar.panelLabel")}
                                onClick={handleActivitiesClick}
                            />
                        </div>
                        <div id="tileTour5">
                            <div className="btn-desktop-hidden">
                                <SidebarBtn
                                    isExpanded={open}
                                    label={"Dashboard"}
                                    icon="home"
                                    tooltip={"Dashboard"}
                                    onClick={() => navigationClicked("/tile")}
                                    tourId="tileTour9"
                                />
                                {actualRoles.includes(ROLE_PRODUCT_TEAM_DASHBOARD) && (
                                    <SidebarBtn
                                        isExpanded={open}
                                        label={"Analytics"}
                                        icon="dashboard_02"
                                        tooltip={"Analytics"}
                                        onClick={() => navigationClicked("/analytics")}
                                    />
                                )}
                                {actualRoles.includes(ROLE_AIDWOPS_DASHBOARD) && (
                                    <SidebarBtn
                                        isExpanded={open}
                                        label="AIDWOPS"
                                        icon="reward_program"
                                        tooltip="AIDWOPS"
                                        onClick={() => navigationClicked("/aidwops")}
                                    />
                                )}
                            </div>
                            {isAdmin && (<>
                                {open && <hr className="sidebar-divider" />}
                                <SidebarBtn
                                    isExpanded={open}
                                    label={t("chatBot.search")}
                                    icon="search"
                                    tooltip={t("chatBot.search")}
                                    onClick={() => navigationClicked("/search-result")}
                                />
                            </>)}
                        </div>
                        {open && <hr className="sidebar-divider" />}
                        <SidebarBtn
                            isExpanded={open}
                            label="my IT support"
                            onClick={() => handleClick(true)}
                            icon="call-center"
                            tooltip="my IT support"
                            tourId="newAiTourSupport"
                        />
                        <SidebarBtn
                            isExpanded={open}
                            label={t("feedbackWidget")}
                            onClick={() => handleClick()}
                            icon="rating-star-empty"
                            tooltip={t("feedbackWidget")}
                        />
                    </div>
                </aside>
            </FocusTrap>
            {open && (
                <div
                    id="sidebar-backdrop"
                    className="backdrop"
                    onClick={() => setOpen(false)}
                    data-testid="backdrop"
                />
            )}
            <div className={`main-content ${open ? "shifted" : ""}`}>
                <div className="me8eon-responsive-container">
                    {children}
                </div>
            </div>
        </div>
    );
}
